// @givmo/app-admin — sign-up details carry-over (ports the webapp `SignUpCubit` `SignUpInfo`).
//
// The email sign-up flow spans four screens — details (name/company/employee-count), email+password,
// the work-email code, and (only when the registration after the code is refused) details again — and
// the company details collected on the first must reach the code screen, which registers the company
// once the code is confirmed (NF-FIX21: the work email is proven BEFORE any company exists). Render-only
// state — no auth logic. The provider path registers straight from the details screen and only reads
// this store when the email path sends a refused registration back there (`registrationError`).
//
// ★ THE STORE LIVES OUTSIDE THE ROUTER TREE, AT MODULE SCOPE — the join store's shape and for the same
// reason (`sign-up-join-context.tsx`). The email/password screen CREATES the Firebase user, and a uid
// change is the app's hard cache boundary: `RoutedApp` (`app.tsx`) remounts the WHOLE `RouterProvider`
// the moment `auth.user.uid` changes, so a store held in React state under that tree would be rebuilt
// empty between the details screen's write and the code screen's read — which, until NF-FIX21, never
// mattered: the details were consumed by the registration in the email/password screen's own submit,
// BEFORE the account existed. Writes are synchronous and reads go through `useSyncExternalStore`. The
// identity boundary clears it on every transition AWAY from a signed-in uid (a sign-out, or one account
// replacing another in this tab), so the next identity never inherits details it did not type; the
// null→A sign-up's own transition does not. A hard refresh clears it too: the code screen then sends
// the caller to the details screen to type them again (`sign-up-otp-screen.tsx`).
//
// ★ THE DETAILS BELONG TO ONE VISITOR (EP-MNT8-FIX1). Keeping them across null→A also kept them for an
// account that never typed them: a signed-out visitor who typed the email sign-up's details and walked
// away left them for the next person to sign in in this tab, whose provider registration then opened
// prefilled with them. So the store records whose they are — the signed-in uid that typed them, or
// `null` for a signed-out visitor — and a screen sees only its own viewer's (`useSignUpDetails(uid)`).
// The ONE way details cross a sign-in is the email sign-up's own: the account it creates adopts them
// (`adopt`, the email/password screen, after the create resolves), so its code screen still registers
// them after the code. Any other account signing in finds none.

import { createContext, use, useMemo, useSyncExternalStore, type ReactNode } from 'react';

export interface SignUpDetails {
  firstName: string;
  lastName: string;
  companyName: string;
  numberOfEmployees: number;
}

/** The store's snapshot — immutable; a write mints a new object so `useSyncExternalStore` sees it. */
export interface SignUpDetailsState {
  /** What the details screen collected; null until it is submitted on the email path. */
  readonly details: SignUpDetails | null;
  /**
   * The server's message when the registration AFTER the code was refused (a duplicate company name, a
   * domain the create refuses). The details screen shows it, prefilled, until its next submit.
   */
  readonly registrationError: string | null;
  /**
   * Whose they are: the signed-in uid that typed them, or `null` for a signed-out visitor — until the
   * account that visitor's email sign-up creates adopts them (see the header).
   */
  readonly owner: string | null;
}

/** The external store: synchronous writes, subscribable reads. */
export interface SignUpDetailsStoreHandle {
  getSnapshot: () => SignUpDetailsState;
  subscribe: (listener: () => void) => () => void;
  /** New details, typed by `owner` (the signed-in uid, or `null` signed out). */
  setDetails: (details: SignUpDetails, owner: string | null) => void;
  setRegistrationError: (message: string | null) => void;
  /** The email sign-up's new account takes the details its signed-out visitor typed (see the header). */
  adopt: (uid: string) => void;
  /** Back to the initial state — the identity boundary (leaving a signed-in uid) and tests call this. */
  reset: () => void;
}

/** What a screen consumes: its viewer's values + the writers. */
export interface SignUpDetailsStore {
  /** The viewer's details, or `null` (none typed, or another visitor's). */
  readonly details: SignUpDetails | null;
  /** The refusal carried for the viewer's details, or `null`. */
  readonly registrationError: string | null;
  /** New details, as the viewer's. */
  setDetails: (details: SignUpDetails) => void;
  setRegistrationError: (message: string | null) => void;
  adopt: (uid: string) => void;
}

const INITIAL_STATE: SignUpDetailsState = { details: null, registrationError: null, owner: null };

/** Create a details store. The app uses ONE, at module scope; tests may build their own for isolation. */
export function createSignUpDetailsStore(): SignUpDetailsStoreHandle {
  let state = INITIAL_STATE;
  const listeners = new Set<() => void>();
  const write = (next: SignUpDetailsState) => {
    if (
      next.details === state.details &&
      next.registrationError === state.registrationError &&
      next.owner === state.owner
    )
      return;
    state = next;
    for (const listener of listeners) listener();
  };
  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    // New details start a fresh attempt: a refusal recorded for the previous ones no longer applies.
    setDetails: (details, owner) => write({ details, registrationError: null, owner }),
    setRegistrationError: (registrationError) => write({ ...state, registrationError }),
    // Only a signed-out visitor's details are adoptable: an account's own stay its own.
    adopt: (uid) => {
      if (state.details !== null && state.owner === null) write({ ...state, owner: uid });
    },
    reset: () => write(INITIAL_STATE),
  };
}

/** The app's one store — deliberately above the router tree (see the header). Exported for tests. */
export const appSignUpDetailsStore: SignUpDetailsStoreHandle = createSignUpDetailsStore();

/** Clear the app's store (leaving a signed-in uid: a sign-out or an account switch; a test's `beforeEach`). */
export function resetSignUpDetailsStore(): void {
  appSignUpDetailsStore.reset();
}

const SignUpDetailsContext = createContext<SignUpDetailsStoreHandle>(appSignUpDetailsStore);

/**
 * Test seam: scope a store to a subtree. The app itself renders NO provider — its store is the
 * module-level one, precisely so the router tree's remounts cannot reset it.
 */
export function SignUpDetailsProvider({
  store,
  children,
}: {
  store?: SignUpDetailsStoreHandle;
  children: ReactNode;
}) {
  // A provider rendered without a store scopes a FRESH one to its subtree (the screens' unit tests).
  const scoped = useMemo(() => store ?? createSignUpDetailsStore(), [store]);
  return <SignUpDetailsContext value={scoped}>{children}</SignUpDetailsContext>;
}

/**
 * Read the sign-up details store (the app's, or a test-scoped one) as `viewer` — the signed-in uid, or
 * `null` signed out: only the viewer's own details and refusal are visible, and what it writes is its.
 */
export function useSignUpDetails(viewer: string | null): SignUpDetailsStore {
  const store = use(SignUpDetailsContext);
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  return useMemo<SignUpDetailsStore>(() => {
    const own = state.owner === viewer;
    return {
      details: own ? state.details : null,
      registrationError: own ? state.registrationError : null,
      setDetails: (details) => store.setDetails(details, viewer),
      setRegistrationError: store.setRegistrationError,
      adopt: store.adopt,
    };
  }, [store, state, viewer]);
}

export { SignUpDetailsContext };
